<template>
    <section class="ai_search" v-if="available">
        <div class="input" :class="{ focused: showSearchRecommend }">
            <ElInput
                v-model="searchValue"
                :placeholder="$t('ai.search.placeholder')"
                size="large"
                @focus="handleFocus"
                @blur="handleBlur"
                @keyup.enter.native="handleSearch"
                maxlength="200"
                clearable
            >
                <template v-slot:append>
                    <button class="button_clear search_btn" @click="handleSearch">
                        <i class="icon-co_search" />
                    </button>
                </template>
            </ElInput>
            <div class="suggestions"
                v-show="showSearchRecommend"
                @mouseenter="isHoveringRecommend = true"
                @mouseleave="isHoveringRecommend = false">
                <div>{{ $t('ai.search.suggestions_title') }}</div>
                <div v-for="suggestion in recommendations"
                    :key="suggestion"
                    class="suggestion"
                    @click="handleSuggestionClick(suggestion)">
                    <i class="icon-co_search" />
                    <span>{{ suggestion }}</span>
                </div>
            </div>
            <i class="icon-co_tips_line tip-btn" @click="handleTipClick"/>
        </div>
        <portal to="body">
            <reo-dialog
                v-if="showDialog"
                :title="$t('ai.search.dialog.title')"
                :label-of-confirm="confirmLabel"
                :label-of-cancel="aiSearchCanWork? undefined : $t('cancel_plan.traffic.cancel')"
                @cancel="handleCloseDialog"
                @confirm="handleDialogConfirm"
                :disableConfirm="aiSearchCanWork && !agreement"
            >
                <div class="dialog-content">
                    <div class="info">
                        <p v-for="content in dialogContents" :key="String(content)"> {{ content }}</p>
                        <button v-if="!showMoreInfo"
                            class="button_clear more-btn"
                            @click="showMoreInfo=true"
                        >{{ $t('show_more') }}<i class="icon-co_add"/>
                        </button>
                        <reo-link
                            :href="config.ai_support"
                            :text="$t('e2ee.learn_more')"
                            target="_blank"
                            v-if="showMoreInfo"
                        />
                    </div>
                    <div v-if="hasAiSubscription && !noDeviceAvailable" class="agreement">
                        <ReoCheckbox
                            v-model="agreement"
                            :isRound="false"
                        />
                        <span>
                            <span class="red">*</span>
                            {{ $t('ai.search.dialog.agreement') }}
                        </span>
                    </div>
                    <section class="device-list" v-if="!noDeviceAvailable">
                        <header>
                            <h4>{{ $t('ai.search.dialog.device_list_title') }}</h4>
                            <button class="button_clear all" @click="handleAllOp">
                                {{ allDevicesChecked? $t('clearAll'):$t('selectAll') }}
                            </button>
                        </header>
                        <ul>
                            <li v-for="device in deviceList" :key="device.uid">
                                <div>{{ getDeviceTitle(device) }}</div>
                                <ReoCheckbox
                                    :isRound="false"
                                    v-model="device.checked"
                                />
                            </li>
                        </ul>
                    </section>
                    <p v-else-if="!hasAiSubscription" class="empty">
                        {{ $t('ai.search.dialog.no_ai_sub') }}
                    </p>
                    <p v-else class="empty">
                        {{ $t('ai.search.dialog.device_list_empty') }}
                    </p>
                </div>
            </reo-dialog>
        </portal>
    </section>
</template>

<script lang="ts">
import Vue from 'vue';
import { Input as ElInput } from 'element-ui';
import ReoDialog from '@/basic/Dialog.vue';
import { getSubDeviceList, getSubscriptions } from '@/api/subscriptions';
import { ISubDeviceWithChecked, ISubscription } from '@reolink-cloud/interface';
import ReoCheckbox from '@/components/reoCheckbox.vue';
import { requestWithErrorHandler } from '@/common/js/utils';
import { getAiAvailability, getSearchRecommendations, modifyDeviceAiConfig } from '@/api/ai';
import { mapGetters } from 'vuex';
import { getDeviceTitle } from '@/utils/business';
import { config } from 'CONFIG';
import { Portal } from 'portal-vue';
import { ICommonRes } from '@/interface';
import ReoLink from '@/basic/Link.vue';
import { apmMonitor } from '@/service/apmMonitor';

export default Vue.extend({
    components: {
        ElInput,
        ReoDialog,
        ReoCheckbox,
        Portal,
        ReoLink
    },
    async created() {

        const featureRes = await getAiAvailability({
            country: this.country,
            features:  'smartAiSearch'
        });

        this.available = Boolean( featureRes.features.smartAiSearch );
        if ( this.available ) {

            let subscriptionRes: ICommonRes<ISubscription>;
            [,this.recommendations, subscriptionRes] = await Promise.all( [
                this.getSupportedDeviceList(),
                getSearchRecommendations( this.lang ),
                getSubscriptions( 'active' )
            ] );
            this.subscriptions = subscriptionRes.items;

        }

    },
    data() {

        return {
            searchValue: '',
            isFocused: false,
            isHoveringRecommend: false,
            blurTimer: null as NodeJS.Timeout | null,
            deviceList: [] as ISubDeviceWithChecked[],
            showSearchTipDialog: false,
            showSearchTipDialogFromTipIcon: false,
            recommendations: [] as string[],
            available: false,
            agreement: false,
            showMoreInfo: false,
            subscriptions: [] as ISubscription[]
        };

    },
    computed:{
        ...mapGetters( ['lang', 'country'] as const ),
        noDeviceAvailable(): boolean {

            return this.deviceList.length === 0;

        },
        noSelectedDevice(): boolean {

            return this.deviceList.filter(( device ) => device.checked ).length === 0;

        },
        hasDeviceCanSearch(): boolean {

            return this.deviceList.some( device =>  device.smartAiEnabled );

        },
        confirmLabel(): string|undefined {

            if ( !this.hasAiSubscription ) {

                return this.$tc( 'subscribe.login_subscribe_button' );

            } else if ( this.noDeviceAvailable ) {

                return this.$tc( 'ai.search.dialog.bind_btn' );

            } else {

                return this.$tc( 'update_plan.confirm_button' );

            }

        },
        showSearchRecommend(): boolean {

            return ( this.isFocused || this.isHoveringRecommend ) && this.searchValue === '';

        },
        allDevicesChecked(): boolean {

            return this.deviceList.every(( device ) => device.checked );

        },
        dialogContents(): string[] {

            const rawContent = this.$t( 'ai.search.dialog.content' ) as unknown as string[];
            if ( this.showMoreInfo ) {

                return rawContent;

            } else {

                return rawContent.slice( 0, 1 );

            }

        },
        hasAiSubscription(): boolean {

            return this.subscriptions.some( sub => sub.aiSupport );

        },
        aiSearchCanWork(): boolean {

            return this.hasAiSubscription && !this.noDeviceAvailable;

        },
        config() {

            return config;

        },
        showDialog(): boolean {

            return this.showSearchTipDialog || this.showSearchTipDialogFromTipIcon;

        }
    },
    watch:{
        showDialog( newVal: boolean ) {

            if ( newVal ) {

                this.deviceList = this.deviceList.map(( device ) => ({
                    ...device,
                    checked: device.smartAiEnabled
                }));

            }

        },
    },
    methods: {
        handleFocus() {

            this.isFocused = true;
            if ( this.blurTimer ) {

                clearTimeout( this.blurTimer );
                this.blurTimer = null;

            }

        },
        handleBlur() {

            // 添加延迟，给用户时间点击建议
            this.blurTimer = setTimeout(() => {

                this.isFocused = false;
                this.blurTimer = null;

            }, 200 );

        },
        handleSuggestionClick( suggestion: string ) {

            this.searchValue = suggestion;
            this.isFocused = false;
            this.isHoveringRecommend = false;
            this.handleSearch();
            if ( this.blurTimer ) {

                clearTimeout( this.blurTimer );
                this.blurTimer = null;

            }

        },
        doSearch() {

            this.$emit( 'search', this.searchValue );

        },
        handleSearch() {

            apmMonitor.changeTransactionName( this.transactionNames.LEVEL1_AI_SEARCH );
            if ( this.hasDeviceCanSearch ) {

                this.doSearch();

            } else {

                this.showSearchTipDialog = true;

            }

        },
        handleAllOp() {

            const allValue = !this.allDevicesChecked;
            for ( const device of this.deviceList ) {

                device.checked = allValue;

            }

        },
        async handleDialogConfirm() {

            if ( !this.hasAiSubscription ) {

                await this.$router.push({
                    name: 'subscribe-cloud'
                });

            } else if ( this.noDeviceAvailable ) {

                await this.$router.push({
                    path: '/user/dashboard/'
                });

            } else {

                await requestWithErrorHandler(() => modifyDeviceAiConfig({
                    devices: this.deviceList.map(( device ) => ({
                        uid: device.uid,
                        smartAiEnabled: device.checked,
                    }))
                }));
                await this.getSupportedDeviceList();
                if ( this.hasDeviceCanSearch ) {

                    if ( !this.showSearchTipDialogFromTipIcon ) {

                        this.doSearch();

                    }

                }
                this.handleCloseDialog();

            }

        },
        async getSupportedDeviceList() {

            this.deviceList = ( await getSubDeviceList({
                ai_support: true
            })).map(( device ) => {

                return {
                    ...device,
                    checked: device.smartAiEnabled
                };

            });

        },
        handleTipClick() {

            this.showSearchTipDialogFromTipIcon = true;

        },
        handleCloseDialog() {

            this.showSearchTipDialog = false;
            this.showSearchTipDialogFromTipIcon = false;

        },
        getDeviceTitle
    }
});
</script>

<style lang="less" scoped>
.ai_search {
    .mobile({
        padding-left: 16px;
    });
    .input {
        position: relative;
        border: @border-radius-level-button;
        display: flex;
        align-items: center;

        &.focused {
            /deep/ input {
                border-bottom-left-radius: 0;
            }
            .search_btn {
                border-bottom-right-radius: 0;
            }
        }

        .search_btn {
            background-color: @b_6;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            border-radius: 0 @border-radius-level-button @border-radius-level-button 0;
            font-size: 24px;
            color: @white;
        }

        .suggestions {
            display: flex;
            flex-direction: column;
            gap: 12px;
            font-size: 12px;
            font-weight: 500;
            position: absolute;
            top: 100%;
            left: 0;
            padding: 12px;
            padding-bottom: 16px;
            background-color: @white;
            width: 100%;
            border-radius: 0 0 @border-radius-level-button @border-radius-level-button;
            width: 326px;
            box-sizing: border-box;

            &::after {
                content: '';
                position: absolute;
                width: 100%;
                left: 0;
                bottom: 0;
                height: calc(100% + 40px);
                box-shadow: 0px 4px 10px 0px @black3;
                border-radius: @border-radius-level-button;
                z-index: -1;
            }

            .suggestion {
                display: flex;
                align-items: center;
                font-size: 14px;
                color: @font_primary;
                cursor: pointer;
                gap: 4px;

                i {
                    font-size: 14px;
                }
            }

        }
        .tip-btn{
            font-size: 20px;
            color: @font_secondary;
            cursor: pointer;
            margin-left: 6px;
        }
    }

    /deep/ .el-input {
        background-color: transparent;
        border-radius: @border-radius-level-button;

        input {
            border-radius: @border-radius-level-button 0 0 @border-radius-level-button;
            border-color: @line;
            width: 286px;

            &:hover {
                border-color: @b_6;
            }

        }

        .el-input-group__append {
            padding: 0;
            width: 40px;
            height: 40px;
            border: 0;
        }
    }

}

/deep/ .dialog-content {
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    .info{
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 12px;
        .more-btn{
            position: absolute;
            bottom: 0;
            right: 0;
            color: @b_6;
            font-size: 16px;
            font-weight: 500;
            line-height: 27px;
            background-color: @bg_02;
            display: flex;
            align-items: center;
        }
    }
    .agreement{
        display: flex;
        gap: 12px;
        padding-top: 24px;
        border-top: 1px solid @line;
        font-size: 14px;
        line-height: 21px;
        font-weight: 500;
    }
    .device-list {
        background-color: @bg_01;
        padding: 20px;
        border-radius: @border-radius-level-2;
        max-height: 200px;
        overflow-y: auto;

        header{
            display: flex;
            justify-content: space-between;
            h4 {
                font-size: 16px;
                font-weight: 600;
                line-height: 24px;
            }

            .all {
                font-size: 14px;
                font-weight: 500;
                line-height: 24px;
                color: @b_6;
            }
        }

        ul {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-top: 12px;

            li {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 14px;
                line-height: 27px;
                font-weight: 600;
                padding-bottom: 16px;
                border-bottom: 1px solid @line;
                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }

    .empty {
        color: @red_5;
    }
}
</style>
